import Lexer from 'lex';
import _ from 'lodash';

import {
  ARITHMETICAL_OPERATOR_REGEX,
  LEX_TOKEN_TYPES,
  REGEXP,
  REGEXP_MATCH,
} from 'constants/parser';

import { canonicalizeFieldPath, getSchemaValue } from '../schemaPath';

const createRule = pattern => new RegExp(pattern);

const skipFn = _.noop;

export default class LexicalParser {
  constructor(schema) {
    this.schema = schema;
    this.parser = new Lexer();

    this._data = [];

    // ORDER OF RULES IS EXTREMELY IMPORTANT
    this._addSimpleRule(createRule(REGEXP.operator), LEX_TOKEN_TYPES.operator);
    this._addSimpleRule(createRule(REGEXP.date), LEX_TOKEN_TYPES.value);
    this._addSimpleRule(createRule(REGEXP.double), LEX_TOKEN_TYPES.value);
    this._addSimpleRule(createRule(REGEXP.integer), LEX_TOKEN_TYPES.value);
    this._addSimpleRule(createRule(REGEXP.string), LEX_TOKEN_TYPES.value);
    this._addSimpleRule(createRule(REGEXP.bracket), LEX_TOKEN_TYPES.bracket);
    this._addSimpleRule(createRule(REGEXP.bool), LEX_TOKEN_TYPES.value);
    this._addSimpleRule(createRule(REGEXP.separator), LEX_TOKEN_TYPES.separator);
    this.parser.addRule(createRule(REGEXP.space), skipFn);

    this._addSimpleRule(createRule(REGEXP.spec_field), LEX_TOKEN_TYPES.spec_field);

    if (Object.keys(this.schema).length) {
      this._addSimpleRule(
        createRule(
          this.schema.lookups.timeZones.map(zone => `'${_.escapeRegExp(zone)}'`).join('|'),
        ),
        LEX_TOKEN_TYPES.value,
      );

      // Placed DATE in the end of regexp by sorting (DATE_NOW, ..., DATE)
      this._addSimpleRule(
        createRule(
          Object.keys(this.schema.functions)
            .sort((a, b) => b.length - a.length)
            .join('|'),
        ),
        LEX_TOKEN_TYPES.function,
      );
    }

    this._addSimpleRule(createRule(REGEXP.field), LEX_TOKEN_TYPES.field);
    this._addSimpleRule(createRule(REGEXP.anySingleCharacter), LEX_TOKEN_TYPES.anySingleCharacter);
  }

  parse(expression = '') {
    this._data = [];
    this._dataSize = 0;
    this.parser.setInput(expression).lex();

    return this._data;
  }

  _getTypeOfValue(value, type) {
    switch (type) {
      case LEX_TOKEN_TYPES.function: {
        return this.schema.functions[value].returnType;
      }

      case LEX_TOKEN_TYPES.operator: {
        const isArithmetical = new RegExp(ARITHMETICAL_OPERATOR_REGEX).test(value);

        return isArithmetical ? LEX_TOKEN_TYPES.decimal : LEX_TOKEN_TYPES.bool;
      }

      case LEX_TOKEN_TYPES.bool:
        return LEX_TOKEN_TYPES.bool;

      case LEX_TOKEN_TYPES.field: {
        // Casing-tolerant because an array token whose path is mis-cased would otherwise lex with
        // no type, and the array functions bind their iterator off that type (STP-555).
        const type = getSchemaValue({
          entitiesSchema: this.schema.entities,
          path: canonicalizeFieldPath({ entitiesSchema: this.schema.entities, path: value }),
        });

        if (_.isString(type) && type.includes(LEX_TOKEN_TYPES.array)) {
          return LEX_TOKEN_TYPES.array;
        }

        return type;
      }

      case LEX_TOKEN_TYPES.value: {
        const match = Object.entries(REGEXP_MATCH).find(([__, pattern]) =>
          createRule(pattern).test(value),
        );

        return _.get(match, '[0]', 'string');
      }

      default: {
        return '';
      }
    }
  }

  _addToken(token) {
    token.position = {
      start: this._dataSize,
      end: this._dataSize + token.value.length - 1,
    };
    this._dataSize += token.value.length;
    this._data.push(token);
  }

  _save = type => value => {
    const typeOfValue = this._getTypeOfValue(value, type);
    const isQuotedString = type === 'value' && typeOfValue === 'string';

    // remove quotes for typeOfValue === string && type === 'value'
    this._addToken({
      type,
      value: isQuotedString ? value.slice(1, -1) : value,
      typeOfValue,
    });
  };

  _addSimpleRule = (rule, type, handler = this._save(type)) => {
    this.parser.addRule(rule, handler);
  };
}
